/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Box, Menu, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { ActivityLog } from 'types/graphql'

import { routes } from '@redwoodjs/router'

import CallTags, {
  CallActorBadge,
} from 'src/components/ActivityLogs/CallLog/CallTags'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import {
  DOWNLOAD_RECORDING_URL,
  DOWNLOAD_TRANSCRIPT_URL,
} from 'src/graphql/activityLogs.graphql'
import { CallStatus } from 'src/graphql/types/activityLogs'
import {
  ApplicantCallConversionStatus,
  CallConnectedStatus,
} from 'src/graphql/types/callActivity'
import IconThreeDots from 'src/icons/IconThreeDots'
import { getCallDurationSeconds } from 'src/lib/activityLogs/callDuration'
import { isMissedCallStatus } from 'src/lib/activityLogs/callMissedStatus'
import { buildFullName, formatSnakeValue } from 'src/lib/formatters'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import CallConversionBadge from 'src/pages/CallAnalyticsPage/components/CallConversionBadge'
import { cn } from 'src/utils'
import { downloadFile } from 'src/utils/downloadFile'

const DropdownMenu = ({ activityLog }) => {
  const [downloadRecording] = useLazyQuery(DOWNLOAD_RECORDING_URL, {
    onCompleted: (data) => {
      if (data?.getRecordingDownloadUrl?.url) {
        const { url, fileName } = data.getRecordingDownloadUrl
        downloadFile({ url, fileName })
      }
    },
    onError: () => {
      toast('Unable to download recording, please try again.', 'error')
    },
  })

  const [downloadTranscript] = useLazyQuery(DOWNLOAD_TRANSCRIPT_URL, {
    onCompleted: (data) => {
      if (data?.getTranscriptDownloadUrl?.url) {
        const { url, fileName } = data.getTranscriptDownloadUrl
        downloadFile({ url, fileName })
      }
    },
    onError: () => {
      toast('Unable to download transcript, please try again.', 'error')
    },
  })

  const handleRecording = useCallback(() => {
    downloadRecording({
      variables: {
        activityLogId: activityLog.activityLogId,
      },
    })
  }, [activityLog, downloadRecording])

  const handleTranscript = useCallback(() => {
    downloadTranscript({
      variables: {
        activityLogId: activityLog.activityLogId,
      },
    })
  }, [activityLog, downloadTranscript])

  return (
    <Menu shadow="md" position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
          <IconThreeDots className="h-4 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown className="w-40">
        <Menu.Item
          className="text-doubleNickel-gray-700"
          disabled={!activityLog.recordingSid}
          onClick={(e) => {
            e.stopPropagation()
            handleRecording()
          }}
        >
          Download audio recording
        </Menu.Item>
        <Menu.Item
          className="text-doubleNickel-gray-700"
          disabled={!activityLog.transcriptFileUrl}
          onClick={(e) => {
            e.stopPropagation()
            handleTranscript()
          }}
        >
          Download transcript
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

interface CallLogCardProps {
  activityLog: ActivityLog
  handleSelect: (activityLog: ActivityLog) => void
  isSelected: boolean
  showIssues: boolean
}

const CallRow = ({
  activityLog,
  handleSelect,
  isSelected,
  showIssues,
}: CallLogCardProps) => {
  const { handleOpen } = useApplicantDrawerContext()
  const isOutboundCall = activityLog.callDirection === 'OUTBOUND'
  const isVirtualCall = activityLog.callType === 'BLANDAI'
  const label = isOutboundCall ? 'Outbound' : 'Inbound'
  const isMissedCall = isMissedCallStatus(activityLog.callStatus)
  const callStatus = activityLog?.callStatus
  const isLeftVoiceMail = activityLog.callStatus === CallStatus.LEFT_VOICEMAIL
  const callDurationInSeconds = getCallDurationSeconds(activityLog)

  const callInMinutes = Math.floor(callDurationInSeconds / 60)
  const remindingSeconds = callDurationInSeconds % 60
  const isManualRegistered = activityLog.callStatus === null
  const fromNumber = activityLog?.from
    ? formatInternationalPhoneNumber(activityLog?.from) ?? 'Unsupported'
    : 'Unknown'
  const fromApplicant = activityLog?.applicant
    ? buildFullName(activityLog.applicant)
    : fromNumber
  const toNumber = activityLog?.to
    ? formatInternationalPhoneNumber(activityLog?.to) ?? 'Unsupported'
    : 'Unknown'
  const isEmployerCall = !!activityLog.voeId
  // Longest, variable-width chip in the Tags cell: it truncates with an
  // ellipsis when the column is narrow and the tooltip carries the full name.
  const voeTagLabel = `VoE${
    activityLog.applicant ? ` · ${buildFullName(activityLog.applicant)}` : ''
  }`
  const toApplicant = isEmployerCall
    ? activityLog.voe?.companyName ?? toNumber
    : activityLog?.applicant
    ? buildFullName(activityLog.applicant)
    : toNumber
  const employeeContact = activityLog?.employee
    ? buildFullName(activityLog.employee)
    : isOutboundCall
    ? fromNumber
    : toNumber
  const recruiter = isVirtualCall
    ? `Voice Agent${
        activityLog.voiceAgent?.name
          ? ` \u2022 ${activityLog.voiceAgent.name}`
          : ''
      }`
    : employeeContact
  const from = isOutboundCall ? recruiter : fromApplicant
  const to = isOutboundCall ? toApplicant : recruiter
  const isVirtualFrom = isOutboundCall && isVirtualCall
  const isVirtualTo = !isOutboundCall && isVirtualCall

  const handleTranscripts = () => {
    handleSelect(activityLog)
  }

  return (
    <Box
      className={cn(
        'border-bg-doubleNickel-gray-600 grid min-h-10 cursor-pointer grid-cols-9 px-2 py-1 text-xs text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-100',
        isSelected && 'rounded bg-doubleNickel-brand-25'
      )}
      onClick={handleTranscripts}
    >
      <div className="col-span-1 my-auto">
        {dayjs(activityLog.activityDate).isValid()
          ? dayjs(activityLog.activityDate).format('MM/DD/YYYY')
          : 'Invalid Date'}
      </div>
      <div className="col-span-1 my-auto">
        {dayjs(activityLog.activityDate).isValid()
          ? dayjs(activityLog.activityDate).format('hh:mm A')
          : 'Invalid Date'}
      </div>
      <Tooltip
        label={
          fromNumber === 'Unsupported'
            ? activityLog?.from || 'No phone number'
            : fromNumber === 'Unknown'
            ? 'No phone number'
            : fromNumber
        }
        position="top"
      >
        <Box
          className={cn(
            'col-span-1 my-auto w-full overflow-hidden text-ellipsis',
            isVirtualFrom && 'text-doubleNickel-brand-500'
          )}
        >
          <span
            className={cn(
              activityLog.applicant?.applicantId &&
                !isOutboundCall &&
                'hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline'
            )}
            onClick={(e) => {
              if (!isOutboundCall && activityLog.applicant?.applicantId) {
                e.stopPropagation()
                //If the user holds down the meta key (command on mac, control on windows) and clicks on the link, it will open in a new tab
                if (e.metaKey) {
                  window.open(
                    routes.applicantDetails({
                      id: activityLog.applicant.applicantId,
                      tab: 'application',
                    }),
                    '_blank' // Opens in a new tab
                  )
                } else {
                  handleOpen(activityLog.applicant.applicantId)
                }
              }
            }}
          >
            {from}
          </span>
        </Box>
      </Tooltip>
      <Tooltip
        label={
          toNumber === 'Unsupported'
            ? activityLog?.to || 'No phone number'
            : toNumber === 'Unknown'
            ? 'No phone number'
            : toNumber
        }
        position="top"
      >
        <Box
          className={cn(
            'col-span-1 my-auto w-full overflow-hidden text-ellipsis ',
            isVirtualTo && 'text-doubleNickel-brand-500'
          )}
        >
          <span
            className={cn(
              activityLog.applicant?.applicantId &&
                isOutboundCall &&
                'hover:cursor-pointer hover:text-doubleNickel-brand-500 hover:underline'
            )}
            onClick={(e) => {
              if (isOutboundCall && activityLog.applicant?.applicantId) {
                e.stopPropagation()
                //If the user holds down the meta key (command on mac, control on windows) and clicks on the link, it will open in a new tab
                if (e.metaKey) {
                  window.open(
                    routes.applicantDetails({
                      id: activityLog.applicant.applicantId,
                      tab: 'application',
                    }),
                    '_blank' // Opens in a new tab
                  )
                } else {
                  handleOpen(activityLog.applicant.applicantId)
                }
              }
            }}
          >
            {to}
          </span>
        </Box>
      </Tooltip>
      <div className="col-span-1 my-auto">
        {isOutboundCall ? '→' : '←'} {label}
      </div>
      <div
        className={cn(
          (isMissedCall || isLeftVoiceMail) &&
            'font-medium text-doubleNickel-error-500',
          activityLog?.callStatus === CallStatus.COMPLETED &&
            'font-medium text-doubleNickel-success-500',
          'col-span-1 my-auto'
        )}
      >
        {isManualRegistered ? 'Manual' : formatSnakeValue(callStatus)}
      </div>
      <div className="col-span-1 my-auto">
        {!isNaN(callInMinutes)
          ? `${callInMinutes}:${remindingSeconds.toLocaleString('en-US', {
              minimumIntegerDigits: 2,
              useGrouping: false,
            })} min`
          : '0:00 min'}
      </div>
      <div className="col-span-2 my-auto flex flex-row items-center justify-between gap-1 overflow-hidden">
        {/* min-w-0 lets this flex item shrink below its content so the
            right-pinned actor/menu group never gets pushed out; the fixed
            chips are shrink-0 and only the VoE tag gives up width. */}
        <div className="flex min-w-0 flex-row items-center gap-1 overflow-hidden">
          <CallTags activityLog={activityLog} />
          {/* A VOICEMAIL disposition is drawn by CallTags as the same chip a
              recruiting mailbox gets, so it is not repeated here. */}
          {activityLog?.connectedStatus &&
            activityLog.connectedStatus !== CallConnectedStatus.VOICEMAIL && (
              <CallConversionBadge
                status={ApplicantCallConversionStatus.CONNECTED}
                connectedStatus={activityLog.connectedStatus}
                voeFindings={activityLog.voeFindings}
              />
            )}
          {isEmployerCall && (
            <Tag
              variant="neutral"
              size="xs"
              showCloseButton={false}
              className="min-w-0 overflow-hidden"
            >
              {/* Tooltip targets a DOM node, not Tag, which is a plain
                  function component and cannot take Mantine's ref. A bare
                  button (Tailwind preflight strips its chrome and makes it
                  inherit font and color, but resets text-transform, hence
                  `capitalize` again) is natively focusable, and the tooltip
                  opens on focus so the full name is reachable without a
                  mouse. A click still bubbles to the row like before; a tap
                  does too, so touch does not also open the tooltip. */}
              <Tooltip
                label={voeTagLabel}
                position="top"
                withArrow
                events={{ hover: true, focus: true, touch: false }}
              >
                <button type="button" className="truncate capitalize">
                  {voeTagLabel}
                </button>
              </Tooltip>
            </Tag>
          )}
        </div>
        <div className="flex shrink-0 flex-row items-center gap-1">
          <CallActorBadge activityLog={activityLog} />
          {showIssues && (
            <AdminContainer>
              {activityLog.voiceAgentAnalysisResults
                .filter((result) => result.result === 'true')
                .map((result) => (
                  <Tag
                    key={result.voiceAgentAnalysisResultId}
                    variant="error"
                    size="xs"
                    showCloseButton={false}
                  >
                    {formatSnakeValue(result.name)}
                  </Tag>
                ))}
            </AdminContainer>
          )}
          <DropdownMenu activityLog={activityLog} />
        </div>
      </div>
    </Box>
  )
}

export default CallRow
